import { useState, useEffect, useMemo } from "react";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import Button from "../../../components/common/Button/Button";
import Modal from "../../../components/common/Modal/Modal";
import { FiDownload } from "react-icons/fi";
import { MdOutlineFileDownload } from "react-icons/md";
import PieChartGraph from "../../../components/common/Charts/PieChartGraph";
import { getApiInstance } from "../../../utils/api";
import { useZone } from "../../../context/ZoneContext";
import { FilterBar } from "../../../components/filtersNew/filter-barNew";
import ExcluirRaeeZeroWasteToggle from "../../../components/common/Inputs/ExcluirRaeeZeroWasteToggle/ExcluirRaeeZeroWasteToggle";
import { createFilterState } from "../../../lib/reportNew/filter-stateNew";
import {
    useExcelExport,
    buildInformeExportFilters,
    codsFilaDe,
} from "../useExcelExport";
import {
    INDICADORES,
    QUICK_FILTERS_INDICADOR,
    mapaPaises,
    mapaTiposObra,
    normalizarFilas,
    filasVisibles,
} from "../indicadores/definiciones";
import "../cantidadesGeneradas/CantidadesGeneradas.scss";
import "./SegregacionTotal.scss";

const INDICADOR = INDICADORES.segregacionTotal;

const COLOR_SEGREGACION = "#10b981";
const COLOR_MEZCLA = "#9ca3af";

const CODIGOS_MEZCLA = ["170904", "200301", "200307"];

export default function SegregacionTotal() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [datos, setDatos] = useState([]);

    const { selectedZone } = useZone();

    const { isModalExcelOpen, openModal, closeModal, handleExportToExcel, handleExportInformeCompleto, exportando } =
        useExcelExport({
            onError: setError,
            resumenUrl: "api/Informe/CreateReportExcelSegregacionTotalResumen",
        });

    const { filterFields } = INDICADOR;

    const [filterState, setFilterState] = useState(() =>
        createFilterState(QUICK_FILTERS_INDICADOR)
    );

    const [paisesArr, setPaisesArr] = useState([]);
    const [tiposObraArr, setTiposObraArr] = useState([]);

    useEffect(() => {
        const api = getApiInstance();
        const fetchDatos = async () => {
            setLoading(true);
            try {
                const response = await api.get(INDICADOR.datosUrl());
                setDatos(INDICADOR.filasDe(response.data));
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchDatos();

        const fetchPaises = async () => {
            try {
                const response = await api.get(`/api/Paises/GetPaises`);
                setPaisesArr(
                    Array.isArray(response.data) ? response.data : []
                );
            } catch (err) {
                console.error(err);
            }
        };
        fetchPaises();

        const fetchTiposObra = async () => {
            try {
                const response = await api.get(
                    `/api/ObrasTipo/GetAllObrasTipo`
                );
                setTiposObraArr(
                    Array.isArray(response.data) ? response.data : []
                );
            } catch (err) {
                console.error(err);
            }
        };
        fetchTiposObra();
    }, []);

    const mapPaises = useMemo(() => mapaPaises(paisesArr), [paisesArr]);
    const mapTiposObra = useMemo(
        () => mapaTiposObra(tiposObraArr),
        [tiposObraArr]
    );
    const reversePaises = useMemo(
        () =>
            Object.fromEntries(
                paisesArr.map((p) => [String(p.pais), String(p.codPais)])
            ),
        [paisesArr]
    );
    const reverseTiposObra = useMemo(
        () =>
            Object.fromEntries(
                tiposObraArr.map((t) => [
                    String(t.descripcion),
                    String(t.codTipoObra),
                ])
            ),
        [tiposObraArr]
    );

    const datosNormalizados = useMemo(
        () => normalizarFilas(datos, mapPaises, mapTiposObra),
        [datos, mapPaises, mapTiposObra]
    );

    const [excluirRaeeZeroWaste, setExcluirRaeeZeroWaste] = useState(false);

    const datosFiltrados = useMemo(
        () =>
            filasVisibles(
                INDICADOR,
                datosNormalizados,
                filterState,
                excluirRaeeZeroWaste
            ),
        [datosNormalizados, filterState, excluirRaeeZeroWaste]
    );

    const exportFilters = useMemo(() => {
        const quick = filterState.quick || {};
        const anios = Array.isArray(quick.anio) ? quick.anio : [];
        const paises = Array.isArray(quick.pais) ? quick.pais : [];
        const tipos = Array.isArray(quick.tipoObra) ? quick.tipoObra : [];
        return {
            ...buildInformeExportFilters({
                selectedAnio: anios.length ? anios[0] : "all",
                selectedPais: paises.length
                    ? reversePaises[paises[0]] ?? paises[0]
                    : "all",
                selectedTipoObra: tipos.map((t) => reverseTiposObra[t] ?? t),
                selectedZonas: quick.codZona,
            }),
            /* Lo que manda de verdad: las filas que se están viendo. Los filtros
               de arriba se quedan por compatibilidad, pero el backend ignora
               país, año y tipo de obra en cuanto recibe esto. */
            codsFila: codsFilaDe(datosFiltrados),
        };
    }, [filterState, reversePaises, reverseTiposObra, datosFiltrados]);

    const { datosTarta, pctSegregacion, pctMezcla } = useMemo(() => {
        const esRNP = (item) => item.peligroso === false;

        const esCodigoMezcla = (item) =>
            CODIGOS_MEZCLA.includes(String(item.codigo ?? "").trim());

        const tRNP = datosFiltrados.reduce(
            (sum, item) => (esRNP(item) ? sum + (Number(item.peso) || 0) : sum),
            0
        );
        const tMezcla = datosFiltrados.reduce(
            (sum, item) =>
                esRNP(item) && esCodigoMezcla(item)
                    ? sum + (Number(item.peso) || 0)
                    : sum,
            0
        );

        const pctSeg = tRNP > 0 ? ((tRNP - tMezcla) / tRNP) * 100 : 0;
        const pctMez = tRNP > 0 ? (tMezcla / tRNP) * 100 : 0;

        const datosTarta = [];
        if (pctSeg > 0) {
            datosTarta.push({ name: "Segregación total", value: pctSeg });
        }
        if (pctMez > 0) {
            datosTarta.push({ name: "Mezcla", value: pctMez });
        }

        return { datosTarta, pctSegregacion: pctSeg, pctMezcla: pctMez };
    }, [datosFiltrados]);

    const renderFilters = () => (
        <div className="filters-section-common">
            <FilterBar
                rows={datosNormalizados}
                state={filterState}
                onChange={setFilterState}
                resultCount={datosFiltrados.length}
                totalCount={datosNormalizados.length}
                fields={filterFields}
                excludeFields={INDICADOR.excludeFields}
                headerExtra={
                    <ExcluirRaeeZeroWasteToggle
                        activo={excluirRaeeZeroWaste}
                        onChange={setExcluirRaeeZeroWaste}
                    />
                }
            />
        </div>
    );

    return (
        <div className="segregacion-total-page">
            <PageLayout
                title="Informe de Segregación Total"
                subtitle="Porcentaje de segregación total frente a mezcla por obra"
                actions={[
                    <Button
                        key="export"
                        componentType="button"
                        variant="filled"
                        startIcon={<FiDownload />}
                        onClick={openModal}
                    >
                        Exportar a excel
                    </Button>,
                ]}
            >
                <div className="tab-content-with-filters">
                    {renderFilters()}

                    <div className="cantidades-generadas-content">
                        <div className="graficas-card">
                            <div className="charts-section">
                                <div className="chart-container pie-chart-container">
                                    <div className="chart-header valorizacion-header">
                                        <div className="valorizacion-header__left">
                                            <h3>Segregación Total</h3>
                                            <p className="segregacion-formula">
                                                % Segregación Total = ((t RNP –
                                                (t 170904 + t 200301 + t
                                                200307)) / t RNP) * 100
                                            </p>
                                        </div>
                                        <div className="valorizacion-header__percentage">
                                                {Number(pctSegregacion.toFixed(4))}%
                                        </div>
                                    </div>
                                    <div className="chart-wrapper">
                                        {loading ? (
                                            <div
                                                className="informe-chart-skeleton"
                                                aria-hidden="true"
                                            >
                                                <div className="informe-chart-skeleton__donut" />
                                                <div className="informe-chart-skeleton__lines">
                                                    <div className="informe-chart-skeleton__line" />
                                                    <div className="informe-chart-skeleton__line informe-chart-skeleton__line--medium" />
                                                    <div className="informe-chart-skeleton__line informe-chart-skeleton__line--short" />
                                                </div>
                                            </div>
                                        ) : (
                                            <PieChartGraph
                                                data={datosTarta}
                                                colors={[
                                                    COLOR_SEGREGACION,
                                                    COLOR_MEZCLA,
                                                ]}
                                            />
                                        )}
                                    </div>
                                    <div className="segregacion-legend">
                                        <div className="segregacion-legend__item">
                                            <span
                                                className="segregacion-legend__dot"
                                                style={{
                                                    background: COLOR_SEGREGACION,
                                                }}
                                            />
                                            <span className="segregacion-legend__label">
                                                Verde: % Segregación total
                                            </span>
                                            <span className="segregacion-legend__value">
                                            {Number(pctSegregacion.toFixed(4))}%
                                            </span>
                                        </div>
                                        <div className="segregacion-legend__item">
                                            <span
                                                className="segregacion-legend__dot"
                                                style={{
                                                    background: COLOR_MEZCLA,
                                                }}
                                            />
                                            <span className="segregacion-legend__label">
                                                Gris: % Mezcla
                                            </span>
                                            <span className="segregacion-legend__value">
                                                {Number(pctMezcla.toFixed(4))}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {error && <div className="error-message">{error}</div>}

                <Modal
                    isOpen={isModalExcelOpen}
                    onClose={closeModal}
                    title="Exportar informe a Excel"
                    actions={[
                        <Button
                            key="export-resumen"
                            className="export-excel__btn export-excel__btn--primary"
                            onClick={() =>
                                handleExportToExcel("resumen", exportFilters)
                            }
                            startIcon={<MdOutlineFileDownload />}
                            disabled={exportando}
                        >
                            {exportando === "resumen"
                                ? "Generando..."
                                : "Informe Segregación Total"}
                        </Button>,
                        <Button
                            key="export-completo"
                            className="export-excel__btn export-excel__btn--secondary"
                            onClick={() =>
                                handleExportInformeCompleto({ filterState, excluirRaeeZeroWaste })
                            }
                            startIcon={<MdOutlineFileDownload />}
                            disabled={exportando}
                        >
                            {exportando === "completo"
                                ? "Generando..."
                                : "Informe Completo"}
                        </Button>,
                    ]}
                >
                    <div className="export-excel__actions"></div>
                </Modal>
            </PageLayout>
        </div>
    );
}
